/**
 * zod schema ของทุก MQTT payload — ต้องตรงกับ docs/MQTT_PROTOCOL.md §2 เป๊ะ
 * รวมถึงคีย์สั้น (p,s,x,y,f,a,fr,st,t) ห้ามเปลี่ยนชื่อโดยไม่ bump version ใน prefix
 */
import { z } from "zod";

const sceneId = z.union([
  z.literal(0), z.literal(1), z.literal(2),
  z.literal(3), z.literal(4), z.literal(5),
]);
const styleId = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]);
const bit = z.union([z.literal(0), z.literal(1)]);

/** 2.1 input — มือถือ → NB · 30 Hz */
export const InputMsg = z.object({
  p: z.string(),
  s: sceneId,
  l: bit, r: bit, u: bit, d: bit,
  /** ปุ่มกระโดดกดค้าง (ใช้คุมความสูง) */
  j: bit,
  /** ตัวนับการกด — เพิ่ม 1 ทุกครั้งที่กดใหม่ · QoS 0 แพ็กเก็ตหายได้ ต้องอ่านจากตัวนับ */
  jp: z.number().int().nonnegative(),
  /**
   * ตัวนับต่อย/เตะ กติกาเดียวกับ jp — optional + default 0
   * เพื่อให้มือถือรุ่นก่อนหน้า (ไม่มีปุ่มพวกนี้) ยังส่ง input ผ่าน schema ได้
   */
  pp: z.number().int().nonnegative().default(0),
  kp: z.number().int().nonnegative().default(0),
  seq: z.number().int().nonnegative(),
  t: z.number(),
});
export type InputMsg = z.infer<typeof InputMsg>;

/** 2.2 spawn — มือถือ → NB */
export const SpawnMsg = z.object({
  p: z.string(),
  s: sceneId,
  x: z.number(),
  y: z.number(),
  st: styleId,
  /** true = NB นี้รันฟิสิกส์ · false = ghost วาดตาม sync เท่านั้น */
  auth: z.boolean(),
  /** true = เล่นอนิเมชันประกอบตัวลงพื้น (ฉาก 0 หลังลงทะเบียน) */
  warp: z.boolean().default(false),
  t: z.number(),
});
export type SpawnMsg = z.infer<typeof SpawnMsg>;

/** 2.3 release — มือถือ → NB */
export const ReleaseMsg = z.object({
  p: z.string(),
  s: sceneId,
  t: z.number(),
});
export type ReleaseMsg = z.infer<typeof ReleaseMsg>;

export const AnimName = z.enum(["idle", "walk", "jump", "fall", "punch", "kick"]);
export type AnimName = z.infer<typeof AnimName>;

/** 2.4 sync — มือถือ → NB (ghost) · 15 Hz · x เป็นลบได้ */
export const SyncMsg = z.object({
  p: z.string(),
  s: sceneId,
  x: z.number(),
  y: z.number(),
  /** facing: -1 ซ้าย, 1 ขวา */
  f: z.number(),
  a: AnimName,
  fr: z.number().int(),
  /** alpha ที่จอนี้ควรวาด · ไม่มี = 1 */
  blend: z.number().min(0).max(1).default(1),
  t: z.number(),
});
export type SyncMsg = z.infer<typeof SyncMsg>;

/** 2.4b pos — NB → มือถือ · 10 Hz · มือถือเก็บค่านี้ในฐานะ database */
export const PosMsg = z.object({
  p: z.string(),
  s: sceneId,
  x: z.number(),
  y: z.number(),
  vx: z.number(),
  vy: z.number(),
  /** onGround */
  g: bit,
  a: AnimName,
  f: z.number(),
  t: z.number(),
});
export type PosMsg = z.infer<typeof PosMsg>;

/** 2.4c edge — NB → มือถือ */
export const EdgeMsg = z.object({
  p: z.string(),
  s: sceneId,
  side: z.enum(["left", "right"]),
  x: z.number(),
  y: z.number(),
  t: z.number(),
});
export type EdgeMsg = z.infer<typeof EdgeMsg>;

/** 2.4d act — NB → มือถือ (NB ตรวจ overlap เอง เพราะฟิสิกส์อยู่ที่นี่) */
export const ActMsg = z.object({
  p: z.string(),
  s: sceneId,
  k: z.enum(["coin", "fruit", "pellet", "bump", "land", "emote"]),
  v: z.number(),
  x: z.number(),
  y: z.number(),
  t: z.number(),
});
export type ActMsg = z.infer<typeof ActMsg>;

const StyleEntry = z.object({
  url: z.string().url(),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
  frames: z.number().int().positive(),
});

/** 2.5 profile — RETAINED · มือถือ publish เอง (Cloudflare ไม่แตะ MQTT) */
export const ProfileMsg = z.object({
  p: z.string(),
  nick: z.string().max(24).optional(),
  styles: z.object({
    "1": StyleEntry,
    "2": StyleEntry,
    "3": StyleEntry,
    // optional — โปรไฟล์ retained รุ่นก่อนมีแค่ 3 สไตล์ ต้องยัง parse ผ่าน
    // จอที่ไม่เจอ "4" จะ fallback ไปใช้ "3" เอง
    "4": StyleEntry.optional(),
  }),
  frameOrder: z.array(z.string()),
  createdAt: z.number(),
  expiresAt: z.number(),
});
export type ProfileMsg = z.infer<typeof ProfileMsg>;

/** 2.6 screen/{scene}/status — RETAINED */
export const ScreenStatusMsg = z.object({
  s: sceneId,
  up: z.boolean(),
  host: z.string().optional(),
  build: z.string().optional(),
  fps: z.number().optional(),
  players: z.number().int().optional(),
  t: z.number(),
});
export type ScreenStatusMsg = z.infer<typeof ScreenStatusMsg>;

/** 2.7 sys/cmd */
export const SysCmdMsg = z.discriminatedUnion("cmd", [
  z.object({ cmd: z.literal("reload") }),
  z.object({ cmd: z.literal("clearPlayers") }),
  z.object({ cmd: z.literal("setDebug"), v: z.boolean() }),
  z.object({ cmd: z.literal("goto"), s: sceneId }),
]);
export type SysCmdMsg = z.infer<typeof SysCmdMsg>;

/**
 * parse payload แบบไม่ throw — MQTT payload มาจากภายนอก
 * ข้อความพังต้องถูกทิ้งเงียบๆ ไม่ใช่ทำให้จอ crash
 */
export function safeParse<T extends z.ZodTypeAny>(
  schema: T,
  raw: Uint8Array | string,
): z.infer<T> | null {
  try {
    const text = typeof raw === "string" ? raw : new TextDecoder().decode(raw);
    if (!text.trim()) return null;
    const r = schema.safeParse(JSON.parse(text));
    return r.success ? r.data : null;
  } catch {
    return null;
  }
}

/** JSON UTF-8 ไม่มี whitespace ตามสเปก */
export function encode(v: unknown): string {
  return JSON.stringify(v);
}
